import * as React from 'react'
import { useMemo } from 'react'
import { Formik, Field } from 'formik'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import { Button, TextInputField } from './form'

import { colors, fonts, media } from '../utils/style'
import { createForgotPasswordFormValidation } from '../utils/validation'

import css from 'styled-jsx/css'

interface Props {
  onSubmit: (values: any, actions: any) => void
}

const { className: linkClassName, styles: linkStyles } = css.resolve`
  a {
    font-family: ${fonts.IBMPlex};
    font-size: 14px;
    line-height: 1;

    color: ${colors.grayText};
  }

  @media (min-width: ${media.desktop}px) {
    a {
      font-size: 16px;
    }
  }
`

export default function ForgotPasswordForm({ onSubmit }: Props) {
  const t = useTranslations()
  const validationSchema = useMemo(() => createForgotPasswordFormValidation(t), [t])

  return (
    <React.Fragment>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit} noValidate>
            <h1>{t('forgotPasswordPage.title')}</h1>

            <div className="field">
              <Field
                name="email"
                component={TextInputField}
                type="email"
                label={t('forgotPasswordPage.form.email')}
              />
            </div>

            <div className="actions">
              <div className="secondary-action">
                <div className="label">
                  {t('authSecondaryActions.alreadyRegistered')}
                </div>
                <Link href="/" className={linkClassName}>
                  {t('authSecondaryActions.login')}
                </Link>
              </div>
              <div className="primary-action">
                <Button
                  disabled={formProps.isSubmitting}
                  onClick={() => formProps.handleSubmit()}
                  type="submit"
                >
                  {formProps.isSubmitting
                    ? t('forgotPasswordPage.form.submitting')
                    : t('forgotPasswordPage.form.submit')}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>

      {linkStyles}

      <style jsx>{`
        h1 {
          margin: 0 0 34px 0;

          font-family: ${fonts.IBMPlex};
          font-weight: bold;
          line-height: 28px;
          font-size: 24px;
          text-align: left;

          color: #14145a;
        }

        .field {
          margin-bottom: 20px;
        }

        .actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 26px;
        }

        .actions > * {
          flex: 1;
        }

        .secondary-action .label {
          margin-bottom: -3px;

          font-family: ${fonts.IBMPlex};
          font-size: 12px;
          line-height: 1;

          color: ${colors.inputText};
        }

        @media (min-width: ${media.desktop}px) {
          h1 {
            margin: 0 -20px 37px;

            line-height: 58px;
            font-size: 50px;
            text-align: center;
          }
        }
      `}</style>
    </React.Fragment>
  )
}
